/**
 * What the unified map remembers between visits: the last query string the user
 * left a customer's map on.
 *
 * The map's own state lives entirely in the URL — this slice exists only so the
 * sidebar "Map" link can return someone to the filters and viewport they were
 * last using for that customer instead of a reset map. Nothing here is
 * authoritative: a direct hit on a map URL ignores it, and losing it costs one
 * default map render.
 *
 * Contains:
 * - rememberMapSearch — record a customer's latest map query string
 * - selectLastMapSearch — read it back for a customer
 */
import { createSlice } from '@reduxjs/toolkit';

const initialState = { lastSearchByCustomer: {} };

const slice = createSlice({
  name: 'map',
  initialState,
  reducers: {
    rememberMapSearch: (state, action) => {
      const { customerId, search } = action.payload ?? {};
      if (customerId == null) return;
      state.lastSearchByCustomer[String(customerId)] = search ?? '';
    },
  },
});

export const { rememberMapSearch } = slice.actions;

/**
 * The query string this customer's map was last left on, or `''` when the map
 * has not been opened for them in this session.
 *
 * @param {string|number} customerId
 * @returns {(state: object) => string}
 */
export const selectLastMapSearch = customerId => state => state.map.lastSearchByCustomer[String(customerId)] ?? '';

export default slice.reducer;
